import React from 'react'
import Backdrop from '@mui/material/Backdrop'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ErrorBoundary } from 'react-error-boundary'

import { useSearchQuery } from 'src/hooks/useSearchQuery'

const lazyComponentCache = new Map<
  string,
  React.LazyExoticComponent<React.ComponentType<any>>
>()

const ModalLoadingFallback = () => (
  <Backdrop
    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 10 }}
    open
  >
    <CircularProgress color="inherit" />
  </Backdrop>
)

const ModalLoadErrorFallback = (props: {
  modal?: string
  onClose: () => void
}) => {
  const { modal, onClose } = props

  return (
    <Backdrop
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 10 }}
      open
      onClick={onClose}
    >
      <Paper
        elevation={0}
        onClick={(event) => event.stopPropagation()}
        sx={{
          width: 320,
          maxWidth: 'calc(100vw - 32px)',
          p: 4,
          borderRadius: '12px',
          border: '1px solid rgba(36, 50, 61, 0.08)',
          boxShadow: '0 8px 24px rgba(34, 47, 62, 0.12)',
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography fontSize={16} fontWeight={600}>
              Could not open modal
            </Typography>
            <Typography color="text.secondary" fontSize={13}>
              {modal
                ? `The ${modal} modal failed to load.`
                : 'Modal failed to load.'}
            </Typography>
          </Stack>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </Stack>
      </Paper>
    </Backdrop>
  )
}

const AsyncModal = React.memo((props: { modal: string }) => {
  const { modal } = props

  if (!lazyComponentCache.has(modal)) {
    lazyComponentCache.set(
      modal,
      React.lazy(() => import(`./${modal}`))
    )
  }

  const ModalComponent = lazyComponentCache.get(modal)!

  return <ModalComponent />
})

const Modal = React.memo((props: { modal?: string; onClose: () => void }) => {
  const { modal, onClose } = props

  if (!modal) {
    return <></>
  }

  return (
    <ErrorBoundary
      resetKeys={[modal]}
      fallbackRender={() => (
        <ModalLoadErrorFallback modal={modal} onClose={onClose} />
      )}
    >
      <React.Suspense fallback={<ModalLoadingFallback />}>
        <AsyncModal modal={modal} />
      </React.Suspense>
    </ErrorBoundary>
  )
})

export const SearchAsyncModal = () => {
  const { query, removeQuery } = useSearchQuery()
  const timeout = React.useRef<NodeJS.Timeout | undefined>()
  const [modal, setModal] = React.useState(query.modal)

  const handleClose = React.useCallback(
    () => removeQuery(['modal']),
    [removeQuery]
  )

  React.useEffect(() => {
    if (timeout.current) {
      clearInterval(timeout.current)
    }

    if (query.modal === undefined) {
      timeout.current = setTimeout(() => {
        setModal(undefined)
      }, 1000)
    } else {
      setModal(query.modal)
    }
  }, [query.modal])

  return <Modal modal={modal} onClose={handleClose} />
}
